<template>
  <div class="calabozo-container">
    <div class="flex flex-row w-full justify-content-between align-items-center">
      <div class="flex flex-column">
        <h1 class="primaryFont title">
          {{ !selectedDetenido ? "Listado de Detenidos" : "Detalle del Detenido" }}
        </h1>
        <TabView :activeIndex="tabIndex" @tab-change="onTabChange" :scrollable="false">
          <TabPanel v-for="(tabLabel, idx) in tabs" :key="idx" :header="tabLabel" />
        </TabView>
      </div>
      <div class="flex align-items-center justify-content-end w-6">
        <CalabozoFilters
          v-if="!selectedDetenido"
          :applied-filters="calabozoStore.getAppliedFilters()"
          @filtrarDetenidos="handleFiltrarDetenidos"
        />
        <div v-else class="flex align-items-center">
          <Button class="volver-listado-btn mr-2" @click="handleVolverListado">
            Volver al listado
          </Button>
          <Button v-if="tabIndex !== 1" class="liberar-det-btn" @click="handleLiberarDetenido">
            <i class="pi pi-user mr-2" />
            {{ actionButtonLabel }}
          </Button>
        </div>
      </div>
    </div>

    <div v-if="!initialId">
      <CalabozoTable @ver-detalle="handleVerDetalle" />
    </div>
    <div v-else>
      <CalabozoDetail :key="initialId" :detencionID="initialId" @volver="handleVolverListado" />
    </div>

    <CalabozoDetenidoModal
      :key="`${initialId}_${selectedDetenido?.EstadoActual}`"
      ref="detenidoModalRef"
      :detenido="selectedDetenido"
      @success="handleDetenidoModalSuccess"
    />
  </div>
</template>

<script setup>
import { useBreadcrumbStore } from "@/store/Pinia/useBreadcrumbStore";
import { useRouterCompat, useStoreCompat } from "@/composables";
import { computed, onMounted, ref, nextTick, watch } from "vue";
import { storeToRefs } from "pinia";
import {
  CalabozoTable,
  CalabozoFilters,
  CalabozoDetail,
  CalabozoDetenidoModal,
} from "@/components/Calabozo/index.js";
import { useCalabozoStore } from "@/store/Pinia/useCalabozoStore";

const store = useStoreCompat();
const breadcrumbStore = useBreadcrumbStore();
const { router, route } = useRouterCompat();

const calabozoStore = useCalabozoStore();
const { selectedDetenido } = storeToRefs(calabozoStore);

const usuario = ref(store.state.user);
const isSuperAdmin = usuario.value.userdata.tipoUsuario === "Super User";

const tabs = ["En Calabozo", "Liberados", "Detenidos recibidos"];
const tabIndex = ref(0);

const estadoByTabIndex = ["EN_CALABOZO", "LIBERADO", "PENDIENTE"];

const detenidoModalRef = ref(null);

const updateRouteQuery = (newTabIndex, detenidoId) => {
  const query = { ...route.query };

  if (typeof newTabIndex === "number") {
    query.tabIndex = String(newTabIndex);
  } else {
    delete query.tabIndex;
  }

  if (detenidoId) {
    query.id = String(detenidoId);
  } else {
    delete query.id;
  }

  router.replace({ query });
};

const updateBreadcrumbs = () => {
  const items = [
    { label: "Calabozo", to: { path: route.path, query: { tabIndex: route.query.tabIndex } } },
  ];
  if (route.query.id) {
    items.push({ label: "Detalle Detenido", to: route.fullPath });
  }
  breadcrumbStore.setItems(items);
};

const actionButtonLabel = computed(() => {
  if (!selectedDetenido.value) return "";
  if (selectedDetenido.value.EstadoActual === "PENDIENTE") return "Registrar detenido";
  return "Liberar detenido";
});
const initialId = computed(() => {
  return route.query.id ? route.query.id : null;
});

const onTabChange = async (event) => {
  const newIndex = event.index;

  if (newIndex === tabIndex.value) return;

  tabIndex.value = newIndex;

  const estado = estadoByTabIndex[newIndex];
  if (estado) {
    calabozoStore.setEstadoFilter(estado);
  }

  const currentFilters = {
    search: calabozoStore.search,
    direct: calabozoStore.directFilters,
  };

  await nextTick();
  await calabozoStore.getDetenciones(currentFilters);
  updateRouteQuery(newIndex, null);
};

const handleFiltrarDetenidos = async (payload) => {
  await calabozoStore.getDetenciones(payload || {});
};

const handleVerDetalle = (row) => {
  calabozoStore.clearSelectedDetenido();
  updateRouteQuery(tabIndex.value, row.DetencionID);
};

const handleVolverListado = () => {
  calabozoStore.clearSelectedDetenido();
  updateRouteQuery(tabIndex.value, null);
};

const handleLiberarDetenido = () => {
  if (!selectedDetenido.value || !detenidoModalRef.value) return;

  const mode = selectedDetenido.value.EstadoActual === "EN_CALABOZO" ? "egreso" : "ingreso";
  detenidoModalRef.value.open(mode);
};

const handleDetenidoModalSuccess = async ({ mode }) => {
  if (mode === "ingreso") {
    await nextTick();
    tabIndex.value = 0;
    const estado = estadoByTabIndex[0];
    if (estado) {
      calabozoStore.setEstadoFilter(estado);
    }

    calabozoStore.clearSelectedDetenido();
    updateRouteQuery(tabIndex.value, null);
    return;
  }

  if (mode === "egreso") {
    await nextTick();
    tabIndex.value = 1;
    const estado = estadoByTabIndex[1];
    if (estado) {
      calabozoStore.setEstadoFilter(estado);
    }

    const detenidoId =
      selectedDetenido.value && selectedDetenido.value.DetencionID
        ? selectedDetenido.value.DetencionID
        : null;
    calabozoStore.clearSelectedDetenido();
    updateRouteQuery(tabIndex.value, detenidoId);
    if (detenidoId) {
      await calabozoStore.loadDetenidoDetalle(detenidoId, true);
    }
  }
};

onMounted(async () => {
  const initialTabIndex = Number.parseInt(route.query.tabIndex, 10);

  if (!Number.isNaN(initialTabIndex) && initialTabIndex >= 0 && initialTabIndex < tabs.length) {
    await nextTick();
    tabIndex.value = initialTabIndex;
  }

  const initialEstado = estadoByTabIndex[tabIndex.value];
  if (initialEstado) {
    calabozoStore.setEstadoFilter(initialEstado);
  }

  updateBreadcrumbs();

  if (isSuperAdmin) {
    store.dispatch("siis/setDependencias");
  }
});

watch(
  () => [route.query.id, route.query.tabIndex],
  () => {
    updateBreadcrumbs();
  }
);
</script>

<style scoped>
.calabozo-container {
  width: 100%;
  padding: 0 2rem;
}
.title {
  font-weight: 700;
  font-style: Bold;
  font-size: 28px;
  line-height: 32px;
  letter-spacing: 0%;
  vertical-align: middle;
  color: #000000;
}

.subtitle {
  margin-top: 1rem;
  font-weight: 600;
  font-size: 20px;
  line-height: 28px;
}

.volver-listado-btn {
  display: flex;
  align-items: center;
  padding: 0.5rem 1.25rem;
  border-radius: 6px;
  background-color: #ffffff;
  color: #0b5195;
  border: 1px solid #0b5195;
  font-weight: 500;
}

.volver-listado-btn:hover {
  background-color: #f1f5f9;
  color: #0b5195;
}

.liberar-det-btn {
  display: flex;
  align-items: center;
  padding: 0.5rem 1.5rem;
  border-radius: 6px;
  background-color: #0b5195;
  color: #ffffff;
  border: 1px solid #0b5195;
  font-weight: 500;
}

.liberar-det-btn:hover {
  background-color: #0b5195;
  opacity: 0.9;
}

::v-deep .p-tabview .p-tabview-nav li.p-highlight .p-tabview-nav-link {
  color: #3758f9 !important;
  border: none !important;
  outline: none !important;
  border-bottom: 2px solid #2563eb !important;
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-style: Medium;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: center;
}

::v-deep .p-tabview .p-tabview-nav li .p-tabview-nav-link:focus {
  box-shadow: none !important;
  outline: none !important;
}

::v-deep .p-tabview .p-tabview-nav li .p-tabview-nav-link {
  border: none !important;
  border-bottom: 2px solid transparent !important;
}

::v-deep .p-tabview .p-tabview-panels {
  padding: 1em 0 !important;
}
</style>
